import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS, ROLE_COLORS } from "../lib/constants";
import { X, Send } from "lucide-react";
import { toast } from "sonner";

export default function ChatDrawer({ open, onClose }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/messaggi");
      setMessages(data);
    } catch (e) {
      console.error("Chat: caricamento messaggi fallito", e);
    }
  };

  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post("/messaggi", { testo: text.trim() });
      setText("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/60 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />
      <div
        data-testid="chat-drawer"
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[400px] bg-slate-900 border-l border-slate-800 flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-800">
          <div>
            <div className="font-display font-black text-slate-50">Chat team</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Comunicazioni interne</div>
          </div>
          <button data-testid="close-chat-btn" onClick={onClose} className="text-slate-400 hover:text-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <p className="text-center text-slate-500 text-sm mt-8">Nessun messaggio. Scrivi al tuo team.</p>
          )}
          {messages.map((m) => {
            const mine = m.sender_id === user.id;
            return (
              <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-slate-300">{mine ? "Tu" : m.sender_name}</span>
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${ROLE_COLORS[m.sender_role]}`}>
                    {ROLE_LABELS[m.sender_role]}
                  </span>
                </div>
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${mine ? "bg-primary text-slate-950" : "bg-slate-800 text-slate-100"}`}>
                  {m.testo}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <div className="p-3 border-t border-slate-800 flex gap-2">
          <input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Scrivi un messaggio..."
            className="flex-1 bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-primary"
          />
          <button
            data-testid="chat-send-btn"
            onClick={send}
            className="px-3 py-2 rounded-md bg-primary text-slate-950 hover:opacity-90 transition-opacity"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );
}
